import {Injectable} from '@angular/core';
import {
    HttpRequest,
    HttpHandler,
    HttpEvent,
    HttpInterceptor,
    HttpErrorResponse,
    HttpResponse,
} from '@angular/common/http';
import {catchError, map} from 'rxjs/operators';
import {tap, Observable, of, throwError} from 'rxjs';
import {Router} from '@angular/router';
import { AzureAuthService } from '../auth/azure-auth.service';
import { ToastrService } from 'ngx-toastr';
import { AppAuthService } from '../auth/auth.service';
import { UserSessionService } from '../auth/user-session.service';
import { ApiError, isInvalidGrant, resolveHttpErrorMessage } from './http-error.util';

@Injectable()
export class AzureApiInterceptor implements HttpInterceptor {
    constructor(
        private router: Router,
        private azureAuthService: AzureAuthService,
        private toastr: ToastrService,
        private appAuthService: AppAuthService,
        private userData: UserSessionService
    ) {
    }

    intercept(
        request: HttpRequest<any>,
        next: HttpHandler
      ): Observable<HttpEvent<any>> {
        
        const authHeaderName = 'Authorization';
        const authToken = this.azureAuthService.getAuthToken();
        // Add the Authorization header if the token is available and not already present
        if (authToken && !request.headers.has(authHeaderName)) {
          request = request.clone({
            headers: request.headers.set(authHeaderName, authToken),
          });
        }
    
        // Handle the HTTP request and response
        return next.handle(request).pipe(
          map((event: HttpEvent<any>) => {
            // Handle successful responses
            if (event instanceof HttpResponse) {
              const messageType =
                typeof event.body?.messageType === 'string'
                  ? event.body.messageType.toLowerCase()
                  : '';
    
              // Handle error messages
              if (messageType === 'failed') {
                this.toastr.error(event.body.message, 'Error Response');
                // ApiError => the catchError below won't toast this again.
                throw new ApiError(event.body.message, event.status);
              }
    
              // Handle specific messages and statuses
              if (event.status === 200) {
                const message = event.body.Message;
                if (message === 'No records found') {
                  this.toastr.success('No records found', '');
                } else if (message === "User don't have rights to access this page") {
                  this.toastr.error("User don't have rights to access this page", '');
                  this.router.navigate(['auth/login']);
                }
              }
            }
            return event;
          }),
          catchError((error: any) => {
            // Already reported (inner interceptor, or the 'failed' body above).
            if (error instanceof ApiError) {
              return throwError(() => error);
            }
            const errorMessage = this.getServerErrorMessage(error);
            this.toastr.error(errorMessage, '');
            return throwError(() => new ApiError(errorMessage, error?.status ?? 0, error));
          })
        );
      }

      /** Prefer the message the backend actually sent over Angular's generic one. */
      private getServerErrorMessage(error: HttpErrorResponse): string {
        if (isInvalidGrant(error)) {
          this.appAuthService.logoutUser();
          return error.error?.error_description || 'Session expired. Please log in again.';
        }
        return resolveHttpErrorMessage(error);
      }
}
